import { t } from '../../../lib/i18n';
import { type ClientBid, type SortOption, type User } from '../types';
import { ClientListItem } from './client-list-item';

type Stat = { total: number; active: number; maxBid: number };

interface ClientListProps {
  users: User[];
  bids: ClientBid[];
  sessionUserId?: string;
  sort: SortOption;
  isLoading?: boolean;
  isError?: boolean;
}

const HEADER_CELL =
  'font-mono text-[10px] leading-[13px] tracking-[0.12em] text-cf-ink-subtle uppercase';

/**
 * The client table (`lg`) / card stack (below it).
 *
 * The desktop artboard fixes the grid at `2fr 2fr 110 130 150 40` with an
 * 880px floor and lets the section scroll horizontally, so a narrow laptop
 * scrolls the table rather than crushing the contact column. The header row and
 * every data row must therefore repeat the same track list — they are separate
 * grids, not one table, exactly as the artboard draws them.
 */
export function ClientList({
  users,
  bids,
  sessionUserId,
  sort,
  isLoading = false,
  isError = false,
}: ClientListProps) {
  // Renders every user the caller passed in — no additional exclusion here.
  // This list and the header counts in `clients-page.tsx` / `use-client-filters.ts`
  // MUST be derived from the same array, or the header's total drifts from the
  // number of rows actually on screen (the `filterCounts.all` vs `visibleUsers`
  // split used to subtract the session user from one but not the other).
  //
  // The session user's OWN row still renders — hiding it made a real client
  // (who happened to be the operator's own dual-purpose admin/client account)
  // silently vanish from `/clients` with no indication why. Instead their row
  // is marked "Це ви" and its one mutation (`ClientListItem`'s verification
  // toggle) is disabled, so an admin can't accidentally re-verify/un-verify
  // themselves from the management list — the actual reason this exclusion
  // existed.
  const visibleUsers = users.filter((u): u is User & { _id: string } => !!u._id);

  const userBidStats = visibleUsers.reduce<Record<string, Stat>>((acc, user) => {
    const userBids = bids.filter((b) => b.userId === user._id);
    const activeBids = userBids.filter((b) => ['Won', 'Under Review'].includes(b.status));
    const maxBid = userBids.reduce((max, b) => Math.max(max, b.bid), 0);
    acc[user._id] = { total: userBids.length, active: activeBids.length, maxBid };
    return acc;
  }, {});

  const EMPTY_STAT: Stat = { total: 0, active: 0, maxBid: 0 };
  const statOf = (id: string): Stat => userBidStats[id] ?? EMPTY_STAT;

  const sortedUsers = [...visibleUsers].sort((a, b) => {
    if (sort === 'name:asc') {
      return ((a.firstName || '') + (a.lastName || '')).localeCompare(
        (b.firstName || '') + (b.lastName || ''),
        'uk-UA',
      );
    }
    if (sort === 'name:desc') {
      return ((b.firstName || '') + (b.lastName || '')).localeCompare(
        (a.firstName || '') + (a.lastName || ''),
        'uk-UA',
      );
    }
    if (sort === 'createdAt:desc') {
      const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return dateB - dateA;
    }
    if (sort === 'createdAt:asc') {
      const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return dateA - dateB;
    }
    const aStats = statOf(a._id);
    const bStats = statOf(b._id);
    if (aStats.active && !bStats.active) return -1;
    if (bStats.active && !aStats.active) return 1;
    return bStats.maxBid - aStats.maxBid;
  });

  return (
    <section className="flex flex-col gap-3 lg:block lg:gap-0 lg:overflow-x-auto lg:rounded-cf-lg lg:border lg:border-cf-line lg:bg-cf-surface">
      <div className="hidden border-b border-cf-line-soft px-[18px] py-2.5 lg:grid lg:min-w-[880px] lg:grid-cols-[minmax(0,2fr)_minmax(0,2fr)_110px_130px_150px_40px] lg:gap-4">
        <span className={HEADER_CELL}>{t('clients.list.columns.client')}</span>
        <span className={HEADER_CELL}>{t('clients.list.columns.contacts')}</span>
        <span className={HEADER_CELL}>{t('clients.list.columns.registered')}</span>
        <span className={HEADER_CELL}>{t('clients.list.columns.verification')}</span>
        <span className={`${HEADER_CELL} text-right`}>{t('clients.list.columns.bids')}</span>
        <span />
      </div>

      {isLoading ? (
        <Placeholder text={t('clients.loading')} />
      ) : isError ? (
        <Placeholder text={t('clients.list.failed')} />
      ) : sortedUsers.length === 0 ? (
        <Placeholder text={t('clients.list.empty')} />
      ) : (
        sortedUsers.map((user) => (
          <ClientListItem
            key={user._id}
            user={user}
            bidCount={statOf(user._id).total}
            activeBidCount={statOf(user._id).active}
            isSelf={user._id === sessionUserId}
          />
        ))
      )}
    </section>
  );
}

function Placeholder({ text }: { text: string }) {
  return (
    <p className="rounded-cf-lg border border-cf-line bg-cf-surface px-4 py-10 text-center text-[13px] text-cf-ink-subtle lg:rounded-none lg:border-0">
      {text}
    </p>
  );
}
